import type { KpowHistoryPoint, KpowScore } from 'react_pages/AmericanPowerIndex/api/kpowApi';

/**
 * The API-decorated display fields shared by the composite, per-component score,
 * and history point. Label formatters take this shape so they read the backend's
 * even-threshold + rounding rules rather than re-deriving from raw signed_value.
 */
type Displayable = Pick<KpowScore, 'display_party' | 'display_value'>;

/** Public KPOW axis bound. The index is published on a -50 (D) to +50 (R) scale. */
export const KPOW_AXIS_MAX = 50;

/** Shared time constants for stats math, chart scrub baselines, and request flooring. */
export const HOUR_MS = 60 * 60 * 1000;
export const DAY_MS = 24 * HOUR_MS;
export const WEEK_MS = 7 * DAY_MS;
export const MONTH_MS = 30 * DAY_MS;

/** Magnitude below which a delta reads as neutral ("Flat") rather than leaning to a party. */
export const FLAT_THRESHOLD = 0.05;

/** Chart time-range options exposed by the period selector, in tab order. */
export type KpowPeriod = 'All' | '1D' | '1W' | '1M' | 'YTD' | 'QTD';
export const KPOW_PERIODS: KpowPeriod[] = ['All', '1D', '1W', '1M', 'YTD', 'QTD'];

/** Display labels for the period tabs. */
export const PERIOD_LABELS: Record<KpowPeriod, string> = {
    All: 'All',
    '1D': '1 Day',
    '1W': '1 Week',
    '1M': '1 Month',
    YTD: 'YTD',
    QTD: 'QTD',
};

/**
 * Start-of-window timestamp (ms) for a period. Trailing periods count back from now;
 * YTD/QTD anchor to the start of the calendar year / quarter.
 */
const periodCutoff = (period: Exclude<KpowPeriod, 'All'>): number => {
    const now = Date.now();
    if (period === '1D') return now - DAY_MS;
    if (period === '1W') return now - WEEK_MS;
    if (period === '1M') return now - MONTH_MS;
    const today = new Date(now);
    if (period === 'YTD') return new Date(today.getFullYear(), 0, 1).getTime();
    // QTD: first day of the current calendar quarter.
    return new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1).getTime();
};

/**
 * Slice `points` to the window for the selected period. 'All' returns the full series.
 * If a window would leave fewer than two points (e.g. a gap in recent data), fall back
 * to the full series so the line never collapses.
 */
export const windowPointsByPeriod = (points: KpowHistoryPoint[], period: KpowPeriod): KpowHistoryPoint[] => {
    if (period === 'All') return points;
    const cutoff = periodCutoff(period);
    const windowed = points.filter((p) => pointDate(p.end_period_ts).getTime() >= cutoff);
    return windowed.length >= 2 ? windowed : points;
};

/** History point timestamps arrive from the API as Unix seconds; convert to a Date. */
export const pointDate = (unixSeconds: number): Date => new Date(unixSeconds * 1000);

/** The most recent point at least `ageMs` before `anchorUnixSeconds`, or the oldest point if none qualifies. */
export const pointBefore = (
    points: KpowHistoryPoint[],
    anchorUnixSeconds: number,
    ageMs: number,
): KpowHistoryPoint | null => {
    if (points.length === 0) return null;
    // Pure integer math on the raw unix seconds — this scan runs once per pointer move
    // while scrubbing a ~4k-point series, so no per-element Date allocations.
    const cutoffSeconds = anchorUnixSeconds - ageMs / 1000;
    for (let i = points.length - 1; i >= 0; i -= 1) {
        if (points[i].end_period_ts <= cutoffSeconds) return points[i];
    }
    return points[0];
};

/** The most recent point at least `ageMs` old (anchored on "now"), or the oldest point if none is. */
export const pointAtAge = (points: KpowHistoryPoint[], ageMs: number): KpowHistoryPoint | null =>
    pointBefore(points, Date.now() / 1000, ageMs);

/** Map a signed value (-50 D .. +50 R) to its 0-100% position on the D→R scale. */
export const signedToPercent = (value: number): number => {
    const clamped = Math.max(-KPOW_AXIS_MAX, Math.min(KPOW_AXIS_MAX, value));
    return ((clamped + KPOW_AXIS_MAX) / (KPOW_AXIS_MAX * 2)) * 100;
};

/** Value-first label, 1 decimal: "+12.0 R" / "+3.4 D" / "Even". Reads API display fields. */
export const formatScore = (s: Displayable): string => {
    if (s.display_party === 'Even') return 'Even';
    return `+${s.display_value.toFixed(1)} ${s.display_party}`;
};

/** Value-first label, 2 decimals: "+12.02 R" / "+3.40 D" / "Even". Reads API display fields. */
export const formatScoreCompact = (s: Displayable): string => {
    if (s.display_party === 'Even') return 'Even';
    return `+${s.display_value.toFixed(2)} ${s.display_party}`;
};

/** Gauge pill: "+12.02 Republican lean" / "+3.40 Democrat lean" / "Even". Reads API display fields. */
export const formatLean = (s: Displayable): string => {
    if (s.display_party === 'Even') return 'Even';
    const word = s.display_party === 'R' ? 'Republican' : 'Democrat';
    return `+${s.display_value.toFixed(2)} ${word} lean`;
};

/**
 * Client-side delta label, e.g. "+2.2 R" / "+0.4 D" / "Flat". The API doesn't
 * compute deltas (1D change, 1W change), so we still derive party + magnitude
 * from the raw difference here.
 */
export const formatDelta = (delta: number): string => {
    if (Math.abs(delta) < FLAT_THRESHOLD) return 'Flat';
    return `+${Math.abs(delta).toFixed(1)} ${delta > 0 ? 'R' : 'D'}`;
};

/** Short stat-style date label, e.g. "May 21". */
export const formatStatDate = (unixSeconds: number): string =>
    pointDate(unixSeconds).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/**
 * Scrub headline date replacing a live indicator while the cursor is parked on a
 * historical reading, e.g. "Jun 12, 2026". Cached formatter — this runs once per
 * pointer move while scrubbing.
 */
const SCRUB_DATE_FORMAT = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
export const formatScrubDate = (unixSeconds: number): string => SCRUB_DATE_FORMAT.format(pointDate(unixSeconds));

/** Long annotation-style date label, e.g. "May 21, 2026". */
export const formatLongDate = (unixSeconds: number): string =>
    pointDate(unixSeconds).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

const humanizeName = (name: string): string =>
    name
        .split('_')
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');

/** Display labels for KPOW components; the API returns only the snake_case name. */
const KPOW_COMPONENT_LABELS: Record<string, string> = {
    presidential: 'Presidential Party',
    senate_control: 'Senate Control',
    house_control: 'House Control',
    house_margin: 'House Margin of Victory',
    senate_margin: 'Senate Margin of Victory',
    shutdown: 'Government Shutdown Risk',
};

/** Composition row order, matching the indices design. */
const KPOW_COMPONENT_ORDER = Object.keys(KPOW_COMPONENT_LABELS);

export const formatComponentName = (name: string): string => KPOW_COMPONENT_LABELS[name] ?? humanizeName(name);

/** Sort key for a component name; unknown names sort last, preserving API order among themselves. */
export const componentOrder = (name: string): number => {
    const idx = KPOW_COMPONENT_ORDER.indexOf(name);
    return idx === -1 ? KPOW_COMPONENT_ORDER.length : idx;
};
